import React, { useEffect } from 'react';
import { COLOR_BASE_BRAND_BLACK, COLOR_BASE_BRAND_WHITE } from '@audi/audi-ui-design-tokens';
import styled from 'styled-components';
import { useServicesContext, Deviations, VehicleBasic, VehicleDetail } from '@oneaudi/vtp-shared';

import { HighLightItem } from '../interface/highlights.interfaces';
import ConsumptionItem from './ConsumptionItem';
import SimpleItem from './SimpleItem';
import WarrantyItem from './WarrantyItem';
import EmissionItem from './EmissionItem';
import { audiLightTheme, AudiPlatformProvider } from '@audi/audi-ui-react-v2';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { CombinedConfig } from '../FeatureHubAppDefinition';
import { registerImpressionTracking, trackFeatureAppReady } from '../tracking/tracking';

export interface AudiFeatureAppVtpHighlightsProps {
  vehicle?: VehicleBasic;
  vehicleDetail?: VehicleDetail;
  renderOnOneCMS?: boolean;
}

const DeviationsWrapper = styled.div`
  width: 100%;
  background-color: ${COLOR_BASE_BRAND_WHITE};
  box-shadow:
    200px 0 0 1px ${COLOR_BASE_BRAND_WHITE},
    -100px 0px 0px 1px ${COLOR_BASE_BRAND_WHITE};
  margin: 0 -16px;
  @media (min-width: 375px) {
    margin: 0 -28px;
  }
  @media (min-width: 768px) {
    margin: 0 -40px;
  }
  @media (min-width: 1024px) {
    margin: 0 -60px;
  }
  @media (min-width: 1440px) {
    margin: 0 -96px;
  }
  margin-bottom: calc(
    var(${(props): string => props.theme.responsive.spacing.xxxl}) +
      var(${(props): string => props.theme.responsive.spacing.l}) - var(
        ${(props): string => props.theme.responsive.spacing.xl}
      )
  );
`;

export const HighlightsFAWrapper = styled.div<{ renderOnOneCMS?: boolean }>`
  background-color: ${COLOR_BASE_BRAND_BLACK};
  box-shadow: 100px 0 ${COLOR_BASE_BRAND_BLACK}, -100px 0 ${COLOR_BASE_BRAND_BLACK};
  box-sizing: border-box;
  margin: ${(props): string =>
    !props.renderOnOneCMS
      ? `calc(var(${props.theme.responsive.spacing.xl})) -24.34% calc(var(${props.theme.responsive.spacing.xxxl}))`
      : `calc(var(${props.theme.responsive.spacing.xl})) 0 calc(var(${props.theme.responsive.spacing.xxxl}))`}}

  padding: ${(props): string =>
    !props.renderOnOneCMS
      ? `calc(
      var(${props.theme.responsive.spacing.xxxl}) +
        var(${props.theme.responsive.spacing.l})
    )
    24.34%
    calc(
      var(${props.theme.responsive.spacing.xxxl}) +
        var(${props.theme.responsive.spacing.l}) - var(
          ${props.theme.responsive.spacing.xl}
        )
    )`
      : `calc(
      var(${props.theme.responsive.spacing.xxxl}) +
        var(${props.theme.responsive.spacing.l})
    )
    0
    calc(
      var(${props.theme.responsive.spacing.xxxl}) +
        var(${props.theme.responsive.spacing.l}) - var(
          ${props.theme.responsive.spacing.xl}
        )
    )`};
  overflow: hidden;
`;

HighlightsFAWrapper.displayName = 'HighlightsFAWrapper';

export const HighlightsModuleWrapper = styled.div`
  box-sizing: border-box;
  background-color: ${COLOR_BASE_BRAND_BLACK};
`;
HighlightsModuleWrapper.displayName = 'HighlightsModuleWrapper';

export const HighlightsModule = styled.ul`
  padding: 0px;
  --amountInRow: 2;
  column-gap: var(${(props): string => props.theme.responsive.spacing.l});
  display: grid;
  grid-template: auto / repeat(var(--amountInRow), 1fr);

  @media screen and (min-width: 768px) and (max-width: 1023px) {
    --amountInRow: 3;
  }

  @media screen and (min-width: 1024px) {
    --amountInRow: 4;
    column-gap: var(${(props): string => props.theme.responsive.spacing.xl});
  }
`;
HighlightsModule.displayName = 'HighlightsModule';

export const Highlights: React.FC<AudiFeatureAppVtpHighlightsProps> = ({
  vehicle,
  vehicleDetail,
  renderOnOneCMS,
}) => {
  const configuration = useServicesContext().featureAppConfig as CombinedConfig;
  const itemsFromConfig = configuration.highlightItems || [];
  // clean duplicate 'TechdataKey' entries
  const highlightItemsArray: HighLightItem[] = [
    ...new Map(itemsFromConfig.map((item) => [item.TechdataKey, item])).values(),
  ];

  if (!vehicle) return null;

  const { productDeviations = '' } = vehicle;
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service',
  ) as TrackingServiceV2;
  const localeService = useServicesContext().getAdditionalService(
    'gfa:locale-service',
  ) as LocaleServiceV1;

  const ref = React.useRef(null);
  useEffect(() => {
    trackFeatureAppReady(trackingService);
    registerImpressionTracking(trackingService, ref!);
  }, []);

  const showConsumptionEmission: boolean =
    localeService.countryCode?.toLowerCase() !== 'de' ? true : !!vehicle.envkv2024;

  return (
    <>
      {highlightItemsArray.length > 0 && vehicle && (
        <HighlightsFAWrapper renderOnOneCMS={renderOnOneCMS}>
          <HighlightsModuleWrapper
            data-core-tracking-include="true"
            className="fa-vtp-highlights"
            ref={ref}
          >
            <HighlightsModule className="fa-vtp-highlights-module">
              {highlightItemsArray.map((item: HighLightItem) => {
                if (!item?.TechdataKey) {
                  return null;
                }
                if (item.TechdataKey.includes('ConsumptionDisplay') && showConsumptionEmission) {
                  return (
                    <>
                      <ConsumptionItem item={item} key={item.TechdataKey} vehicle={vehicle} />
                    </>
                  );
                }
                if (
                  item.TechdataKey.includes('Warranty') &&
                  vehicle?.used?.warrantyTypes &&
                  vehicle?.used?.warrantyTypes?.length > 0 &&
                  !item.TechdataKey.includes('Expiry')
                ) {
                  return (
                    <WarrantyItem
                      warranties={vehicle?.used?.warrantyTypes}
                      item={item}
                      key={item.TechdataKey}
                    />
                  );
                }
                if (item.TechdataKey.includes('EmissionDisplay') && showConsumptionEmission) {
                  return <EmissionItem item={item} key={item.TechdataKey} vehicle={vehicle} />;
                }

                return (
                  <SimpleItem
                    item={item}
                    key={item.TechdataKey}
                    vehicle={vehicle}
                    vehicleDetail={vehicleDetail!}
                  />
                );
              })}
            </HighlightsModule>
          </HighlightsModuleWrapper>
        </HighlightsFAWrapper>
      )}
      <DeviationsWrapper>
        <AudiPlatformProvider theme={audiLightTheme}>
          <Deviations productDeviations={productDeviations} />
        </AudiPlatformProvider>
      </DeviationsWrapper>
    </>
  );
};
